import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTableNode,
  $createTableRowNode,
  $getNodeTriplet,
  $isTableSelection,
  INSERT_TABLE_COMMAND,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
} from "lexical";
import { type FC, useCallback, useEffect } from "react";
import { $createDataTableCellNode } from "../nodes/data-table-cell-node";
import { $createDataTableNode } from "../nodes/data-table-node";
import {
  $createStylableTableCellMui,
  $isStylableTableCellMui,
} from "../nodes/stylable-table-cell-mui";
import {
  INSERT_DATA_TABLE_COMMAND,
  INSERT_STYLEABLE_TABLE_CELL_COMMAND,
  INSERT_STYLEABLE_TABLE_COMMAND,
  SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND,
} from "../types/commands";
import { getTableColumnWidths } from "../utils/get-table-column-widths";
import { $insertNode } from "../utils/insert-node";
import { $isInTableNode } from "../utils/is-in-table-node";
import { type StyleableColumnConfig, useStyleableColumns } from "../utils/use-styleable-columns";

// styleable_columns styleableColumnConfig should be compulsory after the flag is removed
export const SingleTablePlugin: FC<{ styleableColumnConfig?: StyleableColumnConfig | null }> = ({
  styleableColumnConfig,
}) => {
  const [editor] = useLexicalComposerContext();
  const stylableColumns = useStyleableColumns();

  const createTableNode = useCallback(
    (
      { columns, rows }: { columns: number; rows: number },
      {
        cellCreator,
        rowCreator,
        tableCreator,
      }: {
        cellCreator: () => TableCellNode;
        rowCreator: () => TableRowNode;
        tableCreator: () => TableNode;
      },
    ): TableNode | undefined => {
      const selection = $getSelection();
      // Ensure we have selected a text range

      if (!$isRangeSelection(selection)) {
        return undefined;
      }
      // Block adding a table to a table
      if ($isInTableNode(selection.anchor.getNode())) {
        return undefined;
      }

      const tableNode = tableCreator();
      editor.update(() => {
        for (let i = 0; i < rows; i += 1) {
          const tableRowNode = rowCreator();
          for (let j = 0; j < columns; j += 1) {
            const tableCellNode = cellCreator();
            const paragraphNode = $createParagraphNode();
            tableCellNode.append(paragraphNode);
            tableRowNode.append(tableCellNode);
          }
          tableNode.append(tableRowNode);
        }

        $insertNode(tableNode);
      });
      return tableNode;
    },
    [editor],
  );

  useEffect(
    () =>
      editor.registerCommand(
        INSERT_DATA_TABLE_COMMAND,
        ({ columns, rows, border, cellPadding }) => {
          const table = createTableNode(
            { columns, rows },
            {
              cellCreator: () => $createDataTableCellNode(),
              rowCreator: () => $createTableRowNode(),
              tableCreator: () => $createDataTableNode(border, cellPadding),
            },
          );

          return !!table;
        },
        1,
      ),
    [editor, createTableNode],
  );

  useEffect(() => {
    // This will initially register to atleast block unallowed table insertions
    // but will be overridden by the stylable columns command if enabled
    if (stylableColumns.enabled) {
      return;
    }
    return editor.registerCommand(
      INSERT_TABLE_COMMAND,
      () => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        return $isInTableNode(selection.anchor.getNode());
      },
      1,
    );
  }, [editor, stylableColumns]);

  useEffect(() => {
    if (!stylableColumns.enabled) {
      return;
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_STYLEABLE_TABLE_COMMAND,
        // default to 50/50 if no column proportions are provided which is the same as current behaviour.
        ({ columnProportions = [50, 50] }) => {
          const tableNode = createTableNode(
            { columns: columnProportions.length, rows: 1 },
            {
              cellCreator: () => $createStylableTableCellMui(styleableColumnConfig),
              rowCreator: () => $createTableRowNode(),
              tableCreator: () => $createTableNode(),
            },
          );

          if (!tableNode) return false;

          tableNode.setColWidths(columnProportions);

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        INSERT_STYLEABLE_TABLE_CELL_COMMAND,
        ({ insertAfter }) => {
          editor.update(() => {
            // With widths set on the table, adding a column takes the widths over 100%.
            // Therefore this needs to calcualte the new widths and set them on the table node after adding a column.
            // Again as we need to make it manually so it uses the StylableTableCellMui node
            const selection = $getSelection();
            if (!($isRangeSelection(selection) || $isTableSelection(selection))) {
              return false;
            }
            const anchorNode = selection.anchor.getNode();
            // Get a ref to our interested bits
            const [currentSelectedTableCell, , tableNode] = $getNodeTriplet(anchorNode);

            // Create our new table cell node
            const newTableCellNode = $createStylableTableCellMui(styleableColumnConfig).append(
              $createParagraphNode().append($createTextNode()),
            );

            // add it to the row
            if (insertAfter) {
              currentSelectedTableCell.insertAfter(newTableCellNode);
            } else {
              currentSelectedTableCell.insertBefore(newTableCellNode);
            }

            // Rejig the table column widths so that when the new column is added
            // Set all the width on the table so that the columns add up to 100%
            // Columns widths on the table do not update automatically when a column is added
            const previousColumnWidths = getTableColumnWidths(tableNode);

            // splice in the new column width which is the same as the selected column
            const selectedColumnIndex = currentSelectedTableCell.getPreviousSiblings().length;
            const newColumnWidth = previousColumnWidths[selectedColumnIndex]!;
            const newColumnWidths = [...previousColumnWidths];
            newColumnWidths.splice(selectedColumnIndex, 0, newColumnWidth);
            const totalWidth = newColumnWidths.reduce((acc, width) => acc + width);
            const newWidths = newColumnWidths.map((width) => (width / totalWidth) * 100);

            tableNode.setColWidths(newWidths);
          });
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND,
        ({ nodeKey, ...paddings }) => {
          editor.update(
            () => {
              const tableCellNode = $getNodeByKey(nodeKey);

              if (!$isStylableTableCellMui(tableCellNode)) {
                return false;
              }

              if (!paddings.individualPadding) {
                tableCellNode.setCommonPadding(paddings.common);
              }
              if (paddings.individualPadding) {
                tableCellNode.setIndividualPadding(paddings);
              }
            },
            {
              discrete: true,
            },
          );
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor, stylableColumns, styleableColumnConfig, createTableNode]);

  return null;
};
